/**
 * Extrait le contexte de l'erreur depuis l'URL ou le message pour afficher un message plus précis
 */
import { organizationDemonstrativeTarget, organizationOwnerPhrase, organizationToPhrase } from "@Front/Utils/publicSiteBranding";

export function extractContextFromMessage(errorMessage: string | null | undefined): {
	resource?: string;
	action?: string;
} {
	if (!errorMessage) {
		return {};
	}

	// Détecter le type de ressource dans le message
	const resourcePatterns = [
		{ pattern: /folder/i, resource: "dossier" },
		{ pattern: /document/i, resource: "document" },
		{ pattern: /file/i, resource: "fichier" },
		{ pattern: /customer|client/i, resource: "client" },
		{ pattern: /user|utilisateur/i, resource: "utilisateur" },
		{ pattern: /office|étude|cabinet|bureau/i, resource: "étude" },
		{ pattern: /share|partage/i, resource: "partage" },
		{ pattern: /third.party|tiers/i, resource: "tiers" },
		{ pattern: /subscription|abonnement/i, resource: "abonnement" },
	];

	for (const { pattern, resource } of resourcePatterns) {
		if (pattern.test(errorMessage)) {
			return { resource };
		}
	}

	return {};
}

const RESOURCE_ACCESS_MESSAGES: Record<string, { title: string; message: string }> = {
	dossier: {
		title: "Accès refusé au dossier",
		message:
			`Vous n'avez pas les droits nécessaires pour accéder ou modifier ce dossier. Vérifiez que vous êtes bien membre ${organizationOwnerPhrase()} ou que le partage a été configuré.`,
	},
	document: {
		title: "Accès refusé au document",
		message:
			"Vous n'avez pas les droits nécessaires pour consulter ou modifier ce document. Seuls les utilisateurs autorisés ou les parties prenantes peuvent y accéder.",
	},
	fichier: {
		title: "Accès refusé au fichier",
		message: "Vous n'avez pas les droits nécessaires pour accéder à ce fichier. Vérifiez vos permissions.",
	},
	client: {
		title: "Accès refusé aux clients",
		message: "Vous n'avez pas les droits nécessaires pour gérer les clients de ce dossier. Contactez votre administrateur.",
	},
	utilisateur: {
		title: "Accès refusé",
		message: "Vous n'avez pas les droits nécessaires pour gérer cet utilisateur. Seuls les administrateurs peuvent effectuer cette action.",
	},
	étude: {
		title: `Accès refusé ${organizationToPhrase()}`,
		message: `Vous n'avez pas accès à ${organizationDemonstrativeTarget()}. Vérifiez que vous êtes bien connecté avec le bon compte.`,
	},
	partage: {
		title: "Accès refusé au partage",
		message:
			`Vous n'avez pas les droits nécessaires pour gérer ce partage de dossier. Seuls les administrateurs ${organizationOwnerPhrase()} peuvent effectuer cette action.`,
	},
	tiers: {
		title: "Accès refusé aux tiers",
		message: "Vous n'avez pas les droits nécessaires pour gérer les tiers de ce dossier. Contactez votre administrateur.",
	},
	abonnement: {
		title: "Accès refusé à l'abonnement",
		message: "Vous n'avez pas les droits nécessaires pour gérer l'abonnement. Seuls les administrateurs peuvent effectuer cette action.",
	},
};

/**
 * Génère un message contextuel pour les erreurs 401/403
 */
export function getContextualAccessMessage(
	errorMessage: string | null | undefined,
	httpStatus?: number,
): import("../types").UserFriendlyError | null {
	if (httpStatus !== 401 && httpStatus !== 403) {
		return null;
	}

	const context = extractContextFromMessage(errorMessage || "");

	if (context.resource) {
		const resourceMessage = RESOURCE_ACCESS_MESSAGES[context.resource];
		if (resourceMessage) {
			return {
				...resourceMessage,
				canRetry: false,
			};
		}
	}

	return null;
}
